import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRIORITIES } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";

export default function TaskModal({ open, onClose, onSaved, task }) {
  const { settings } = useSettings();
  const categories = settings?.categories || [];
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && task) {
      setForm({
        title: task.title || "",
        code: task.code || "",
        notes: task.notes || "",
        priority: task.priority || "medium",
        source: task.source || "note",
        category: task.category || "none",
      });
    }
  }, [open, task]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Give your task a title");
    if (!form.code.trim()) return toast.error("Code can't be empty");
    const maxLen = settings?.max_code_len ?? 24;
    const codeChanged = form.code.trim() !== (task.code || "");
    if (codeChanged && form.code.trim().length > maxLen) return toast.error(`Root must be ${maxLen} characters or fewer`);
    setSaving(true);
    try {
      await api.put(`/tasks/${task.id}`, { ...form, category: form.category === "none" ? "" : form.category });
      toast.success("Task updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold">Edit task</DialogTitle>
          <DialogDescription>Update task details. The root stays unique to you.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input data-testid="edit-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Root</Label>
            <Input data-testid="edit-code-input" value={form.code} maxLength={settings?.max_code_len ?? 24} onChange={(e) => set("code", e.target.value)} className="font-mono font-bold tracking-wide" />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="edit-category-select"><SelectValue placeholder="No category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="edit-notes-input" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="cancel-edit-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-edit-btn" className="bg-primary hover:bg-[#E85D04] text-white font-semibold rounded-lg">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
